/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  AUX_STREAM_MSID,
  ConnectionEvent,
  ConnectionState,
  ErrorCode,
  EXCHANGE_SDP_TIMEOUT,
  IRemoteTrack,
  MAIN_STREAM_MSID,
  MediaType,
  NAME,
  NOT_SUPPORTED_H264,
  PEER_CONNECTION_STATE,
  PublishedUser,
  RemoteAudioTrack,
  RemoteAuxiliaryTrack,
  RemoteStreamType,
  RemoteVideoTrack,
  RtcError,
  SDP_SEMANTICS_UNIFIED_PLAN,
  TRANSPORT_DIRECTION,
  UserRole,
  VideoCodec
} from 'trtc-js-sdk-core';
import Connection from './connection';
import * as SdpParser from './sdp';
import { ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { decodeSupportStatus, isAddTransceiverSupported } from 'trtc-js-sdk-core/src/common/rtc-detection';
import { getMuteStateFromFlag, getReconnectionTimeout, isUndefined } from 'trtc-js-sdk-core/src/utils/utils';
import { RemoteTracksType, SUBSCRIBE_TYPES } from '../type/type';
import { SignalCommand, SignalEvent } from '../signal/constant';
import { before, after, createDecorator } from 'trtc-js-sdk-core/src/utils/decorators/middleware';
import { KV_REPORT_KEY_NETWORK } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import { reportSuccessRate } from 'trtc-js-sdk-core/src/utils/decorators/report-success-rate';
import { DownlinkConnectionOption } from './type';
import AVPlayerStateSyncManager from 'trtc-js-sdk-core/src/common/player/av-player-state-sync-manager';

export function getSubscribedKeys(subscribeState: SubscribeState) {
  return Object.keys(subscribeState).filter(key => subscribeState[key as keyof typeof subscribeState]);
}

/**
 * // FIXME: 下行重连过程中，会频繁触发 track mute unmute
 * // TODO: 下行重连过程中，减少日志打印。
 * 继承自 Connection 类，实现下行重连逻辑
 * 将下行相关功能逻辑从 Connection 中分离
 * @class DownlinkConnection
 * @extends {Connection}
 */
class DownlinkConnection extends Connection implements PublishedUser {
  private _flag = 0;
  isRobot = false;
  role = UserRole.ANCHOR;
  // muteState: MuteState;
  remoteAudioTrack: RemoteAudioTrack;
  remoteVideoTrack: RemoteVideoTrack;
  remoteAuxiliaryTrack: RemoteAuxiliaryTrack;
  avPlayerStateSyncManager: AVPlayerStateSyncManager;

  // subscribeState = {
  //   audio: false,
  //   video: false,
  //   auxiliary: false,
  //   smallVideo: false
  // };
  // 记录下行流的 ssrc，可在上报 monitor 时，根据 ssrc 来区分数据类型
  ssrc = {
    audio: 0,
    video: 0,
    auxiliary: 0
  };

  // 标识当前 connection 是否正在交互 sdp
  private _isSDPExchanging = false;
  private _videoCodec?: VideoCodec | undefined;
  fromType?: number;
  // enableDtx?: boolean;
  get videoCodec() {
    const sdp = this._peerConnection?.remoteDescription?.sdp;
    if (sdp) {
      return sdp.includes('H264') ? VideoCodec.H264 : VideoCodec.VP8;
    }
    return this._videoCodec || VideoCodec.H264;
  }
  set videoCodec(value: VideoCodec) {
    this._videoCodec = value;
  }
  constructor(options: DownlinkConnectionOption) {
    super({ ...options, isUplink: false });
    // 远端用户的推流状态
    // 用于标识远端用户是否有推 audio, video, aux 流
    // this.muteState = options.muteState;
    this.flag = options.flag;
    this.isRobot = options.isRobot || false;
    this.remoteAudioTrack = options.remoteAudioTrack || new RemoteAudioTrack(this._room, this);
    this.remoteVideoTrack = options.remoteVideoTrack || new RemoteVideoTrack(this._room, this);
    this.remoteAuxiliaryTrack = options.remoteAuxiliaryTrack || new RemoteAuxiliaryTrack(this._room, this);
    this.avPlayerStateSyncManager = new AVPlayerStateSyncManager({
      log: this._log,
      audioPlayer: this.remoteAudioTrack.player,
      videoPlayer: this.remoteVideoTrack.player
    });
  }

  get subscribeState(): SubscribeState {
    const state: SubscribeState = { audio: false, video: false, auxiliary: false, smallVideo: false };
    if (this.remoteVideoTrack.isSubscribed) {
      if (this.remoteVideoTrack.mediaType & MediaType.SMALL_VIDEO) {
        state.smallVideo = true;
      } else {
        state.video = true;
      }
    }
    if (this.remoteAudioTrack.isSubscribed) {
      state.audio = true;
    }
    if (this.remoteAuxiliaryTrack.isSubscribed) {
      state.auxiliary = true;
    }
    return state;
  }

  get muteState() {
    return getMuteStateFromFlag(this.flag, this.userId);
  }

  get flag() {
    return this._flag;
  }

  set flag(value: number) {
    if (value === this._flag) return;
    this._flag = value;
    this.remoteAudioTrack?.onFlagChanged();
    this.remoteVideoTrack?.onFlagChanged();
    this.remoteAuxiliaryTrack?.onFlagChanged();
  }

  // 当前是否有主流
  get hasMainStream() {
    return this.muteState.hasAudio || this.muteState.hasVideo || this.muteState.hasSmall;
  }

  // 当前是否有辅流
  get hasAuxStream() {
    return this.muteState.hasAuxiliary;
  }

  /**
   * 判断当前是否订阅了主流
   */
  get isMainStreamSubscribed() {
    return (
      (this.subscribeState.audio ||
        this.subscribeState.video ||
        this.subscribeState.smallVideo) &&
      (this.muteState.hasAudio || this.muteState.hasVideo || this.muteState.hasSmall)
    );
  }

  /**
   * 判断当前是否订阅了辅流
   */
  get isAuxStreamSubscribed() {
    return this.subscribeState.auxiliary && this.muteState.hasAuxiliary;
  }

  /**
   * 判断当前是否订阅了小流
   */
  get isSmallStreamSubscribed() {
    return this.subscribeState.smallVideo && this.muteState.hasSmall;
  }

  /**
   * 判断当前是否订阅了大流
   */
  get isBigStreamSubscribed() {
    return this.subscribeState.video && this.muteState.hasVideo;
  }

  isStreamUnpublished(streamType: RemoteStreamType) {
    if (streamType === NAME.MAIN) {
      return !this.muteState.hasAudio && !this.muteState.hasVideo;
    }
    return !this.muteState.hasAuxiliary;
  }

  initialize() {
    super.initialize();
    this.installEvents();

    this._peerConnection!.ontrack = this.onTrack.bind(this);
  }

  close(reason?: string) {
    super.close(reason);
    // 远端取消推流，将远端推流状态设为 false
    this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);

    this.remoteAudioTrack.close();
    this.remoteVideoTrack.close();
    this.remoteAuxiliaryTrack.close();
    this.avPlayerStateSyncManager.destroy();
    this.uninstallEvents();
  }

  /**
   *
   * @private
   * @memberof Connection
   */
  installEvents() {

  }

  uninstallEvents() {
    this._emitter.removeAllListeners();
  }

  emitConnectionStateChangedEvent(state: ConnectionState) {
    const prevState = this._currentState;
    const result = super.emitConnectionStateChangedEvent(state);
    if (result && prevState !== state) {
      this.remoteVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      this.remoteAuxiliaryTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
    }
    return result;
  }

  /**
   * RTCPeerConnection.ontrack handler
   *
   * @private
   * @param {*} event
   * @memberof Connection
   */
  onTrack(event: RTCTrackEvent) {
    const stream = event.streams[0];
    const { track } = event;

    const streamType = stream.id === MAIN_STREAM_MSID ? NAME.MAIN : NAME.AUXILIARY;
    this._log.debug(`ontrack ${streamType} ${track.kind}`);
    let remoteTrackType: keyof RemoteTracksType = NAME.AUDIO;
    if (track.kind === NAME.VIDEO) {
      remoteTrackType = streamType === NAME.MAIN ? NAME.VIDEO : NAME.AUXILIARY;
    }
    let remoteTrack: IRemoteTrack = this.remoteAudioTrack;
    if (remoteTrackType === NAME.VIDEO) {
      remoteTrack = this.remoteVideoTrack;
    } else if (remoteTrackType === NAME.AUXILIARY) {
      remoteTrack = this.remoteAuxiliaryTrack;
    }
    remoteTrack!.setInputMediaStreamTrack(track);
  }

  /**
   * 给 sdp 增加 rrtr line
   * 规则：在 a=rtcp-fb:xxx 的最后一行追加 a=rtcp-fb:xxx rrtr
   * 作用：提升弱网抵抗能力
   * @private
   * @param {String} sdp
   * @return {String} sdp
   * @memberof Connection
   */
  addRRTRLine(sdp: string) {
    const arr = sdp.split('\r\n');
    // 标记在哪个 index 添加 rrtr line. Map<index, rrtrLine>
    const marks = new Map();
    arr.forEach((str, i) => {
      // 找到 a=rtcp-fb:xxx 的最后一行
      if (/^a=rtcp-fb:/.test(str) && arr[i + 1] && !/^a=rtcp-fb:/.test(arr[i + 1])) {
        marks.set(i + 1, `${str.match(/^a=rtcp-fb:\d+/)![0]} rrtr`);
      }
    });

    const temp = [...marks];
    // 向 SDP 插入 rrtr line
    for (let i = 0; i < temp.length; i++) {
      const [index, rrtrLine] = temp[i];
      arr.splice(index + i, 0, rrtrLine);
    }
    return arr.join('\r\n');
  }

  /**
   * 修复 Chrome h264 开启硬件加速，弱网丢包时拉流出现花屏的问题
   * ref: http://tapd.oa.com/TerWebG/prong/stories/view/1020396022860831661
   * @private
   * @param {String} sdp
   * @return {String} sdp
   * @memberof Connection
   */
  addSPSDescription(sdp: string) {
    const parsedSDP = SdpParser.parse(sdp);
    parsedSDP.media.forEach(item => {
      if (item.type === NAME.VIDEO) {
        item.fmtp.forEach(fmtpItem => {
          fmtpItem.config += ';sps-pps-idr-in-keyframe=1';
        });
      }
    });
    return SdpParser.write(parsedSDP);
  }

  /**
   * 删除如下 ext line
   * a=extmap:4 urn:ietf:params:rtp-hdrext:sdes:mid
   * a=extmap:5 urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id
   * a=extmap:6 urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id
   * 原因：不同浏览器对 extmap 解释不同，导致解包失败，进而导致黑屏。
   * @private
   * @param {String} sdp
   * @return {String} sdp
   */
  removeSDESDescription(sdp: string) {
    const extItemsToRemove = [
      'urn:ietf:params:rtp-hdrext:sdes:mid',
      'urn:ietf:params:rtp-hdrext:sdes:rtp-stream-id',
      'urn:ietf:params:rtp-hdrext:sdes:repaired-rtp-stream-id'
    ];
    const parsedSDP = SdpParser.parse(sdp);
    parsedSDP.media.forEach(item => {
      if (!item.ext) return;
      item.ext = item.ext.filter(extItem => !extItemsToRemove.includes(extItem.uri));
    });
    return SdpParser.write(parsedSDP);
  }

  isSubscriptionStateNotChanged(subscribeState: SubscribeState) {
    return JSON.stringify(subscribeState) === JSON.stringify(this.subscribeState);
  }

  /**
   * 订阅远端
   */
  // 订阅过程中，远端取消推流，subscribe 可能会一直处于 pending 状态，此时需及时 reject。
  @createDecorator<DownlinkConnection>(next => function (this: DownlinkConnection, ...args) {
    return new Promise((resolve, reject) => {
      const onClosed = (reason: string) => {
        this._emitter.off(ConnectionEvent.CLOSED, onClosed);
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_ABORTED,
            data: reason
          })
        }));
      };
      this._emitter.on(ConnectionEvent.CLOSED, onClosed);
      next.apply(this, args).then(resolve, reject)
        .finally(() => {
          this._emitter.off(ConnectionEvent.CLOSED, onClosed);
        });
    });
  })
  async subscribe(subscribeState: SubscribeState, streamType: RemoteStreamType) {
    try {
      // 自动订阅的情况下，接入侧第一次调用 subscribe 接口时，会走到此处。
      // 此时 PeerConnection 尚未连上，等待连接上后再走后续流程
      if (
        this._peerConnection?.connectionState === PEER_CONNECTION_STATE.NEW ||
        this._peerConnection?.connectionState === PEER_CONNECTION_STATE.CONNECTING
      ) {
        await this.waitForPeerConnectionConnected();
      }
      if (this.isSubscriptionStateNotChanged(subscribeState)) {
        if (!this._peerConnection) {
          // 若自动订阅的 PeerConnection 连接失败了，用户重新调用 subscribe 接口重试，会走到此处
          // 此时重新连接 PeerConnection
          this.initialize();
          await this.connect(subscribeState);
        }
        return;
      }

      this._log.info(`subscribe ${streamType} ${JSON.stringify(subscribeState)}`);

      // 已建立下行 或 下行正在交换 SDP，此时再调用 subscribe，则订阅状态变更，不需交互 SDP
      if (this._peerConnection || this._isSDPExchanging) {
        let commandType = SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE;
        // 订阅状态变更，如果 audio, video, aux 都为 false, 则发 unsubscribe 信令
        if (!Object.values(subscribeState).find(item => item === true)) {
          commandType = SUBSCRIBE_TYPES.UNSUBSCRIBE;
        }
        await this.sendSubscription(commandType, subscribeState);
      } else {
        this.initialize();
        await this.connect(subscribeState);
      }
    } catch (error: any) {
      // 远端取消推流导致的订阅失败。
      if (this._room.isJoined && this.isStreamUnpublished(streamType)) {
        this._log.warn(`${error.message} ${JSON.stringify(this.muteState)}`);
        throw new RtcError({
          code: ErrorCode.REMOTE_STREAM_NOT_EXIST,
          message: `remote user ${this.userId} unpublished stream`
        });
      }
      throw error;
    }
  }

  /**
   * 取消订阅远端流
   */
  @reportSuccessRate(KV_REPORT_KEY_NETWORK.UNSUBSCRIBE, false)
  async unsubscribe({
    remoteTracks,
    streamType
  }: { remoteTracks: IRemoteTrack[]; streamType: RemoteStreamType; }) {
    if (this._currentState === ConnectionState.CONNECTED && ((streamType === RemoteStreamType.Main && !this.isMainStreamSubscribed) ||
      (streamType === RemoteStreamType.Aux && !this.isAuxStreamSubscribed))) {
      // 避免重复取消订阅
      this._log.info(`${streamType} stream already unsubscribed`);
      return;
    }

    const subscribeState = { ...this.subscribeState };

    remoteTracks.forEach(remoteTrack => {
      switch (remoteTrack.mediaType) {
        case MediaType.AUDIO:
          subscribeState.audio = false;
          break;
        case MediaType.BIG_VIDEO:
          subscribeState.video = false;
          break;
        case MediaType.SMALL_VIDEO:
          subscribeState.smallVideo = false;
          break;
        case MediaType.AUX_VIDEO:
          subscribeState.auxiliary = false;
          break;
        default: break;
      }
    });

    let commandType = SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE;
    // 当前 peerConnection 还订阅着其他流，此时发 subscribe change 信令。
    // 例如：取消订阅主流，但是当前 peerConnection 还订阅着辅流，则发 subscribe change 信令。
    // 只有当 audio, video, aux 都不订阅时，发 unsubscribe 信令。
    if (!Object.values(subscribeState).find(item => item === true)) {
      commandType = SUBSCRIBE_TYPES.UNSUBSCRIBE;
    }
    this._log.info(`${commandType === SUBSCRIBE_TYPES.UNSUBSCRIBE ? commandType : 'subscribe'} ${streamType} [${getSubscribedKeys(subscribeState)}]`);

    await this.sendSubscription(commandType, subscribeState);

    // audio, video, aux 都不订阅时，关闭 peerConnection
    if (commandType === SUBSCRIBE_TYPES.UNSUBSCRIBE) {
      this.closePeerConnection();
      // 取消订阅后，PeerConnection 已关闭，抛 DISCONNECTED
      this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);
    }
  }

  async unsubscribeDataChannel() {
    // 多 pc 不支持
  }

  sendSubscription(type: SUBSCRIBE_TYPES, subscribeState = this.subscribeState) {
    // https://iwiki.woa.com/pages/viewpage.action?pageId=1109917349#id-%E5%85%B7%E4%BD%93%E4%BF%A1%E4%BB%A4%E5%AE%9A%E4%B9%89-%E5%85%AD%E3%80%81unsubscribe
    let data: {
      audio?: boolean,
      bigVideo?: boolean,
      auxVideo?: boolean,
      smallVideo?: boolean,
      srcTinyId: string,
      srcUserId?: string
    } = {
      srcTinyId: this.tinyId,
      srcUserId: this.userId
    };
    let command: string = SignalCommand.UNSUBSCRIBE;
    let responseCommand: string = SignalEvent.UNSUBSCRIBE_RESULT;
    if (type === SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE) {
      data = {
        audio: subscribeState.audio,
        bigVideo: subscribeState.video,
        auxVideo: subscribeState.auxiliary,
        smallVideo: subscribeState.smallVideo,
        srcTinyId: this.tinyId
      };

      command = SignalCommand.SUBSCRIBE_CHANGE;
      responseCommand = SignalEvent.SUBSCRIBE_CHANGE_RESULT;
    }
    return this._signalChannel
      .sendWaitForResponse({ command, data, responseCommand, timeout: 10000 })
      .then(({ data }: any) => {
        if (data.code !== 0) {
          const error = new RtcError({
            code: data.code,
            message: generateErrorMessage({
              key: ErrorName.ERROR_MESSAGE,
              data: { type, message: data.message }
            })
          });
          this._log.error(error);
          throw error;
        }
      });
  }

  @after(Connection.prototype.afterConnect)
  @before(Connection.prototype.beforeConnect)
  async connect(subscribeState: SubscribeState = this.subscribeState) {
    try {
      // 1. 交换 sdp
      await this.exchangeSDP(subscribeState);
      // 2. 等待 PeerConnection connected
      await this.waitForPeerConnectionConnected();
    } catch (error) {
      this.closePeerConnection(true);
      throw error;
    }
  }

  /**
   * 交换下行 SDP，建立 peerConnection
   * 注意：
   * 1. unified-plan 建立下行需要通过 addTransceiver 开放主流音视频、辅流媒体通道。
   * 2. plan-b 不需要 sdk 处理，后台下发的 answer 会默认开启。
   */
  async exchangeSDP(subscribeState: SubscribeState) {
    try {
      this._isSDPExchanging = true;
      await this.createOffer();
      this._log.info('createOffer success, sending offer');
      const { type, sdp } = this._peerConnection!.localDescription!;
      const data = {
        type,
        sdp,
        srcUserId: this.userId,
        srcTinyId: this.tinyId,
        audio: subscribeState.audio,
        bigVideo: subscribeState.video,
        auxVideo: subscribeState.auxiliary,
        smallVideo: subscribeState.smallVideo
      };

      const result = await this._signalChannel.sendWaitForResponse({
        command: SignalCommand.SUBSCRIBE,
        commandDesc: 'exchange sdp',
        data,
        responseCommand: SignalEvent.SUBSCRIBE_RESULT,
        timeout: EXCHANGE_SDP_TIMEOUT // sdp 比较大，信令超时时间 5s 调整为 10s
      });

      // 没有 await 的连续调用，subscribe 调用会走到此处逻辑。
      // 此时 PeerConnection 已关闭，抛出 error 引导客户调整调用姿势。
      // subscribe(stream);
      // unsubscribe(stream);
      if (!this._peerConnection) {
        const error = new RtcError({
          code: ErrorCode.INVALID_OPERATION,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_CLOSED
          })
        });
        this._log.warn(error);
        throw error;
      }

      await this.onSubscribeResult(result);
      this._isSDPExchanging = false;
    } catch (error) {
      this._isSDPExchanging = false;
      throw error;
    }
  }

  async createOffer() {
    const options: {
      [prop: string]: boolean
    } = {
      voiceActivityDetection: false
    };
    if (isAddTransceiverSupported() && this._sdpSemantics === SDP_SEMANTICS_UNIFIED_PLAN) {
      // 主流音频
      this._peerConnection!.addTransceiver(NAME.AUDIO, {
        direction: TRANSPORT_DIRECTION.RECVONLY
      });
      // 主流视频
      this._peerConnection!.addTransceiver(NAME.VIDEO, {
        direction: TRANSPORT_DIRECTION.RECVONLY
      });
      // 辅流视频
      this._peerConnection!.addTransceiver(NAME.VIDEO, {
        direction: TRANSPORT_DIRECTION.RECVONLY
      });
    } else {
      // 在不支持 addTransceiver 或使用 plan b 的浏览器上，使用如下两个参数，表明下行需要接收音视频数据
      // 避免同时使用 addTransceiver 和如下两个参数。在部分比较老的 ios safari 浏览器上，
      // 共用会导致创建的 offer 出现多余的 mid，进而导致交换 sdp 失败。
      options.offerToReceiveAudio = true;
      options.offerToReceiveVideo = true;
    }

    const offer = await this._peerConnection!.createOffer(options);
    if (offer.sdp) {
      const { isH264DecodeSupported } = await decodeSupportStatus();
      if (!isH264DecodeSupported) {
        this._log.warn('remove h264 desc from sdp');
        offer.sdp = SdpParser.removeH264(offer.sdp);
      }
      offer.sdp = this.addRRTRLine(offer.sdp);
      offer.sdp = this.addSPSDescription(offer.sdp);
      offer.sdp = SdpParser.addStereoDescription(offer.sdp);
      offer.sdp = SdpParser.enableDtx(offer.sdp, this._enableDtx);
      // 删除 unified-plan 的 SDES 相关描述
      if (this._sdpSemantics === SDP_SEMANTICS_UNIFIED_PLAN) {
        offer.sdp = this.removeSDESDescription(offer.sdp);
      }
    }

    await this.setOffer(offer);
  }

  /**
   * 收到订阅回包，包括 answer sdp
   * @param {Object} event
   */
  async onSubscribeResult(event: any) {
    const { code, message = '' } = (event && event.data) || {};
    const { type, sdp } = (event && event.data && event.data.data) || {};
    if (code === NOT_SUPPORTED_H264) {
      throw new RtcError({
        code: ErrorCode.NOT_SUPPORTED_H264,
        message: generateErrorMessage({ key: ErrorName.NOT_SUPPORTED_H264DECODE })
      });
    }
    try {
      if (code !== 0) {
        throw new RtcError({
          code,
          message: generateErrorMessage({
            key: ErrorName.EXCHANGE_SDP_FAILED,
            data: { errMsg: message }
          })
        });
      }
      this._log.debug(`accept remote answer: ${sdp}`);
      await this.setAnswer({ type, sdp });
      this.updateSSRC(sdp);
    } catch (error) {
      this._log.error(error);
      throw error;
    }
  }

  updateSSRC(sdp: string) {
    try {
      const parsedSDP = SdpParser.parse(sdp);
      parsedSDP.media.forEach(item => {
        if (!item.ssrcs) return;
        if (item.type === NAME.AUDIO) {
          const audioSSRC = item.ssrcs.find(item => item.value?.includes(MAIN_STREAM_MSID));
          if (audioSSRC) {
            this.ssrc.audio = Number(audioSSRC.id);
          }
        } else {
          const videoSSRC = item.ssrcs.find(item => item.value?.includes(MAIN_STREAM_MSID));
          const auxiliarySSRC = item.ssrcs.find(item => item.value?.includes(AUX_STREAM_MSID));
          if (videoSSRC) {
            this.ssrc.video = Number(videoSSRC.id);
          }
          if (auxiliarySSRC) {
            this.ssrc.auxiliary = Number(auxiliarySSRC.id);
          }
        }
      });
    } catch (error) {
      // ignore
    }
  }

  // TOOD:
  // getMainStream() {
  //   return this._remoteStreams.get(MAIN_STREAM_MSID);
  // }

  // getAuxStream() {
  //   return this._remoteStreams.get(AUX_STREAM_MSID);
  // }

  getMainStreamVideoTrackId() {
    if (this.remoteVideoTrack && this.remoteVideoTrack.mediaTrack) {
      return this.remoteVideoTrack.mediaTrack.id;
    }
    return '';
  }

  getAuxStreamVideoTrackId() {
    if (this.remoteAuxiliaryTrack && this.remoteAuxiliaryTrack.mediaTrack) {
      return this.remoteAuxiliaryTrack.mediaTrack.id;
    }
    return '';
  }

  /**
   * 下行重连
   * @memberof DownlinkConnection
   */
  async reconnect() {
    const result = super.beforeReconnect();
    if (result < 0) return;

    try {
      // 关闭 peerConnection 开始重连
      this.closePeerConnection();
      this.initialize();
      await this.connect();
      this.stopReconnection();
      this._log.warn('reconnect() success');
    } catch (error) {
      const timeout = getReconnectionTimeout(this._reconnectionCount);
      this._log.warn(`reconnect() timeout, try again after ${timeout / 1000}s`);
      this._reconnectionTimer = setTimeout(() => {
        this.clearReconnectionTimer();
        this.reconnect();
      }, timeout);
    }
  }

  getIsReconnecting() {
    return this._isReconnecting;
  }

  /**
   * @private
   * @memberof DownlinkConnection
   */
  clearReconnectionTimer() {
    if (this._reconnectionTimer !== -1) {
      clearTimeout(this._reconnectionTimer);
      this._reconnectionTimer = -1;
    }
  }

  getCurrentState() {
    return this._currentState;
  }

  /**
   * update end2end delay
   */
  setDelay({ audioDelay, videoDelay }: { audioDelay: number; videoDelay: number }) {
    this.remoteAudioTrack.stat.end2EndDelay = audioDelay;
    this.remoteVideoTrack.stat.end2EndDelay = videoDelay;
  }

  get audioReceiver() {
    return this._peerConnection?.getReceivers()[0] || null;
  }
}

export default DownlinkConnection;
